import * as Sentry from '@sentry/browser';
import { APP_POOL_CONFIG, ENVIRONMENT, POOL_INSTANCE, RELEASE, SENTRY_DSN, defaultLocale } from '@synergy/config';
import { CssBaseline } from '@mui/material';
import { ErrorBoundary, LocalizedProvider } from '@synergy/components';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { IntlProvider } from 'react-intl';
import { Locale } from '@synergy/models';
import { ThemeProvider } from '@mui/material/styles';
import { createRoot } from 'react-dom/client';
import { initSentry } from '@synergy/utils';
import { request } from '@synergy/api';
import { sharedMessagesDE, sharedMessagesDE_CH, sharedMessagesFR } from '@synergy/translations';

import * as serviceWorker from './serviceWorker';
import { App } from './App';
import { loginTheme } from './login-theme';
import messagesDE from './translations/de.json';
import messagesDE_CH from './translations/de-ch.json';
import messagesFR from './translations/fr.json';
import messagesIT from './translations/it.json';

initSentry({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: RELEASE,
    project: 'login-frontend',
    poolInstance: POOL_INSTANCE,
});

const messages: { [locale: string]: { [key: string]: string } } = {
    de: { ...sharedMessagesDE, ...messagesDE },
    'de-ch': { ...sharedMessagesDE, ...sharedMessagesDE_CH, ...messagesDE, ...messagesDE_CH },
    fr: { ...sharedMessagesFR, ...messagesFR },
    'it-ch': { ...sharedMessagesDE, ...messagesIT },
};

document.title = `${APP_POOL_CONFIG.name_short} Login`;

const start = () => {
    const intlDefaultLocale = defaultLocale.replace('_', '-');
    const browserLocale = navigator.language.split('-')[0];
    const mergedMessages = { ...messages[intlDefaultLocale], ...messages[browserLocale] }; // Merge default messages with selected locale
    const htmlLang = Object.keys(Locale).includes(browserLocale) ? browserLocale : defaultLocale;
    request.setRedirectOnAuthError(false);

    const app = (
        <HelmetProvider>
            <ErrorBoundary errorMessage={<span onClick={() => Sentry.showReportDialog()}>Report feedback</span>}>
                <IntlProvider locale={intlDefaultLocale} messages={mergedMessages} defaultLocale={intlDefaultLocale}>
                    <LocalizedProvider locales={[browserLocale as Locale, defaultLocale]}>
                        <Helmet>
                            <html lang={htmlLang} />
                        </Helmet>
                        <ThemeProvider theme={loginTheme}>
                            <CssBaseline />
                            <App />
                        </ThemeProvider>
                    </LocalizedProvider>
                </IntlProvider>
            </ErrorBoundary>
        </HelmetProvider>
    );

    const rootElement = document.getElementById('root');
    if (!rootElement) {
        return;
    }
    const root = createRoot(rootElement);

    root.render(app);

    // If you want your app to work offline and load faster, you can change
    // unregister() to register() below. Note this comes with some pitfalls.
    // Learn more about service workers: https://bit.ly/CRA-PWA
    serviceWorker.unregister();
};

start();
